import type { ReactNode } from 'react';
import {
  formatCurrency,
  formatMaybeDateTime,
  formatPercent,
} from '../SharedAccountBody';
import { FinIntellectModelCode } from '../FinIntellectModelCode';
import type { UserRetailAccount } from '../types';
import type { ComparisonAccount } from './comparisonTypes';
import {
  lookupCompanyName,
  lookupPositionMeta,
  type PerformanceSummary,
  type PositionDiffCell,
} from './comparisonUtils';

const signClass = (value?: number) =>
  value === undefined ? '' : value >= 0 ? 'text-positive' : 'text-negative';

const TT_TITLE = 'mb-1.5 text-[12px] font-semibold text-white/55';
const TT_SUB = 'mb-2 text-[11px] text-white/45';
const TT_ROW =
  'grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-3 py-[3px]';
const TT_LABEL = 'text-[12px] text-white/55';
const TT_VALUE =
  'break-words text-right font-semibold tabular-nums text-foreground';

const CHIP_DOT = 'inline-block h-2 w-2 shrink-0 rounded-full';
const TT_COMPANY = '-mt-1 mb-1.5 text-[12px] font-medium text-foreground';

/** Ticker heading with the company name under it when known (#610). */
const TickerHeading = ({
  symbol,
  companyName,
}: {
  symbol: string;
  companyName?: string;
}) => (
  <>
    <p className={TT_TITLE}>{symbol}</p>
    {companyName && <p className={TT_COMPANY}>{companyName}</p>}
  </>
);

const TooltipRow = ({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: ReactNode;
  valueClass?: string;
}) => (
  <div className={TT_ROW}>
    <span className={TT_LABEL}>{label}</span>
    <span className={`${TT_VALUE}${valueClass ? ` ${valueClass}` : ''}`}>
      {value}
    </span>
  </div>
);

export const portfolioModelTooltipContent = (
  account: UserRetailAccount,
  content?: ComparisonAccount['content'],
) => (
  <>
    <p className={TT_TITLE}>Rebalancing model</p>
    <TooltipRow
      label="Model"
      value={<FinIntellectModelCode model={account.finintellectModel} />}
    />
    <TooltipRow
      label="SignalForge"
      value={account.signal_forge_enabled ? 'On' : 'Off'}
      valueClass={
        account.signal_forge_enabled
          ? 'text-[color:var(--nq-gold-amber)]'
          : ''
      }
    />
    <TooltipRow
      label="Last rebalanced"
      value={formatMaybeDateTime(account.lastRebalancedAt ?? undefined)}
    />
    <TooltipRow
      label="Trading since"
      value={formatMaybeDateTime(
        content?.tradingStartedAt ?? content?.trackRecordStartedAt,
      )}
    />
    <TooltipRow label="Broker" value={account.broker} />
  </>
);

export const vsLeaderColumnTooltipContent = () => (
  <>
    <p className={TT_TITLE}>Vs leader</p>
    <p className={TT_SUB}>
      <strong>Δ</strong> — gap in total return since trading began vs the
      leading portfolio (percentage points).
    </p>
    <p className={TT_SUB}>
      <strong>α</strong> — gap in alpha vs the S&amp;P 500 (SPY) benchmark vs
      the leading portfolio (percentage points).
    </p>
  </>
);

export const sinceStartRowTooltipContent = (
  account: ComparisonAccount,
  summary: PerformanceSummary,
) => {
  const content = account.content;
  const perf = content?.performance?.sinceStart;
  const bench = content?.vsBenchmarkSinceStart;

  return (
    <>
      <p className={TT_TITLE}>Total return since trading begins</p>
      <TooltipRow
        label="Return"
        value={formatPercent(summary.sinceStartPct ?? 0)}
        valueClass={signClass(summary.sinceStartPct)}
      />
      <TooltipRow
        label="Gain"
        value={
          summary.sinceStartAmount !== undefined
            ? `${summary.sinceStartAmount >= 0 ? '+' : ''}${formatCurrency(summary.sinceStartAmount)}`
            : '—'
        }
        valueClass={signClass(summary.sinceStartAmount)}
      />
      {summary.alphaSinceStart !== undefined && (
        <TooltipRow
          label="Alpha vs SPY"
          value={formatPercent(summary.alphaSinceStart)}
          valueClass={signClass(summary.alphaSinceStart)}
        />
      )}
      {bench?.benchmarkAvailable && (
        <TooltipRow
          label="Indexed vs SPY"
          value={formatPercent(bench.relativePct)}
          valueClass={signClass(bench.relativePct)}
        />
      )}
      {perf?.benchmarkReturn !== undefined && (
        <TooltipRow
          label="SPY return"
          value={formatPercent(perf.benchmarkReturn > 1 ? perf.benchmarkReturn / 100 : perf.benchmarkReturn)}
        />
      )}
      <TooltipRow
        label="Track record"
        value={formatMaybeDateTime(
          content?.trackRecordStartedAt ?? content?.tradingStartedAt,
        )}
      />
      <TooltipRow label="Open positions" value={String(summary.positionCount)} />
      <TooltipRow
        label="Unrealized P/L"
        value={formatPercent(summary.unrealizedPLPct ?? 0)}
        valueClass={signClass(summary.unrealizedPLPct)}
      />
    </>
  );
};

export const positionRowTooltipContent = (
  symbol: string,
  cell: PositionDiffCell | undefined,
  accountLabel?: string,
  lastSoldAt?: string,
  companyName?: string,
) => (
  <>
    <TickerHeading symbol={symbol} companyName={companyName} />
    {accountLabel && <TooltipRow label="Portfolio" value={accountLabel} />}
    {cell ? (
      <>
        <TooltipRow label="Weight" value={`${cell.weightPct.toFixed(2)}%`} />
        {cell.qty !== undefined && (
          <TooltipRow label="Qty" value={cell.qty.toLocaleString()} />
        )}
        {cell.avgEntryPrice !== undefined && (
          <TooltipRow label="Avg entry" value={formatCurrency(cell.avgEntryPrice)} />
        )}
        {cell.currentPrice !== undefined && (
          <TooltipRow label="Last price" value={formatCurrency(cell.currentPrice)} />
        )}
        {cell.marketValue !== undefined && (
          <TooltipRow label="Market value" value={formatCurrency(cell.marketValue)} />
        )}
        {cell.unrealizedPLPct !== undefined && (
          <TooltipRow
            label="Unrealized P/L"
            value={formatPercent(cell.unrealizedPLPct)}
            valueClass={signClass(cell.unrealizedPLPct)}
          />
        )}
        {cell.heldSince && (
          <TooltipRow label="Purchased" value={formatMaybeDateTime(cell.heldSince)} />
        )}
        {cell.lastSoldAt && (
          <TooltipRow label="Last sold" value={formatMaybeDateTime(cell.lastSoldAt)} />
        )}
      </>
    ) : (
      <>
        <TooltipRow label="Status" value="Not held" />
        {lastSoldAt && (
          <TooltipRow label="Last sold" value={formatMaybeDateTime(lastSoldAt)} />
        )}
      </>
    )}
  </>
);

export const moverRowTooltipContent = (
  symbol: string,
  cell: PositionDiffCell | undefined,
  accountLabel: string,
  companyName?: string,
) => positionRowTooltipContent(symbol, cell, accountLabel, undefined, companyName);

const formatPurchased = (heldSince?: string) =>
  heldSince ? formatMaybeDateTime(heldSince) : '—';

const TT_TH =
  'border-b border-white/[0.08] px-2 py-[5px] align-middle whitespace-nowrap text-left text-[10px] font-semibold uppercase tracking-[0.03em] text-white/45';
const TT_TH_NUM = `${TT_TH} text-right tabular-nums`;
const TT_TD =
  'border-b border-white/[0.08] px-2 py-[5px] align-middle whitespace-nowrap';
const TT_TD_NUM = `${TT_TD} text-right tabular-nums`;

/** Shared holdings row — table comparing position details across portfolios. */
export const sharedHoldingRowTooltipContent = (
  symbol: string,
  accounts: ComparisonAccount[],
) => (
  <>
    <TickerHeading
      symbol={symbol}
      companyName={lookupCompanyName(accounts, symbol)}
    />
    <p className={TT_SUB}>Compared across {accounts.length} portfolios</p>
    <div className="mt-1 overflow-x-auto">
      <table className="w-full border-collapse text-[11px] leading-[1.35]">
        <thead>
          <tr>
            <th scope="col" className={TT_TH}>
              Portfolio
            </th>
            <th scope="col" className={TT_TH_NUM}>
              Weight
            </th>
            <th scope="col" className={TT_TH_NUM}>
              Unreal. P/L
            </th>
            <th scope="col" className={TT_TH_NUM}>
              Purchased
            </th>
            <th scope="col" className={TT_TH_NUM}>
              Qty
            </th>
            <th scope="col" className={TT_TH_NUM}>
              Avg entry
            </th>
            <th scope="col" className={TT_TH_NUM}>
              Last price
            </th>
            <th scope="col" className={TT_TH_NUM}>
              Mkt value
            </th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((acc) => {
            const cell = lookupPositionMeta(accounts, acc.accountId, symbol);
            return (
              <tr key={acc.accountId}>
                <td className={TT_TD}>
                  <span className="flex max-w-[160px] items-center gap-1.5">
                    <span
                      className={CHIP_DOT}
                      style={{ backgroundColor: acc.color }}
                      aria-hidden
                    />
                    <span
                      className="overflow-hidden text-ellipsis whitespace-nowrap font-medium text-white/[0.88]"
                      title={acc.label}
                    >
                      {acc.label}
                    </span>
                  </span>
                </td>
                {cell ? (
                  <>
                    <td className={TT_TD_NUM}>{cell.weightPct.toFixed(2)}%</td>
                    <td
                      className={`${TT_TD_NUM}${cell.unrealizedPLPct !== undefined ? ` ${signClass(cell.unrealizedPLPct)}` : ''}`}
                    >
                      {cell.unrealizedPLPct !== undefined
                        ? formatPercent(cell.unrealizedPLPct)
                        : '—'}
                    </td>
                    <td className={TT_TD_NUM}>{formatPurchased(cell.heldSince)}</td>
                    <td className={TT_TD_NUM}>
                      {cell.qty !== undefined ? cell.qty.toLocaleString() : '—'}
                    </td>
                    <td className={TT_TD_NUM}>
                      {cell.avgEntryPrice !== undefined
                        ? formatCurrency(cell.avgEntryPrice)
                        : '—'}
                    </td>
                    <td className={TT_TD_NUM}>
                      {cell.currentPrice !== undefined
                        ? formatCurrency(cell.currentPrice)
                        : '—'}
                    </td>
                    <td className={TT_TD_NUM}>
                      {cell.marketValue !== undefined
                        ? formatCurrency(cell.marketValue)
                        : '—'}
                    </td>
                  </>
                ) : (
                  <td colSpan={7} className={`${TT_TD} italic text-white/45`}>
                    Not held
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </>
);
